import React, { useEffect, useRef } from 'react';
import { Button, message, Flex, Typography, Modal, ConfigProvider } from 'antd';
import { THEME } from '../../main';
import { DownloadOutlined } from '@ant-design/icons';
import { useLazyGenerateQualityPacketQuery, } from '../../redux/api/order-line-item';

const { Text } = Typography;

interface ButtonProps {
	lineItemId: string;
	isDisabled?: boolean;
	fileName?: string;
}

export const QualityPacketButton: React.FC<
	ButtonProps
> = ({ lineItemId, isDisabled, fileName }) => {
	const [messageApi, contextHolder] = message.useMessage();

	const [
		triggerReport,
		{ data: data, error: error, isLoading: isLoading },
	] = useLazyGenerateQualityPacketQuery();

	useEffect(() => {
		if (data) {
			const url = URL.createObjectURL(data);

			const fallbackName = `Packet_${lineItemId}.pdf`;
			const composedName = fileName && fileName.trim() !== '' ? fileName : fallbackName;

			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', composedName);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			setTimeout(() => URL.revokeObjectURL(url), 1000);
		}
	}, [data, fileName, lineItemId]);

    const modalShownRef = useRef(false);

    const showMismatchModal = (intake: any, outtake: any) => {
        if (modalShownRef.current) return;
        modalShownRef.current = true;
        Modal.confirm({
            centered: true,
			closable: true,
            title: 'Packet Generation Blocked',
            icon: null,
            content: `Intake (${intake ?? '—'}) and Outtake (${outtake ?? '—'}) quantities do not match. You can reconcile quantities first, or generate the packet anyway.`,
            onOk: () => {
                modalShownRef.current = false;
                handleDownloadReport(true);
            },
			okText: 'Generate anyway',
            onCancel: () => { modalShownRef.current = false; },
			cancelText: 'Reconcile first',
            footer: (_, { OkBtn, CancelBtn }) => (
                <ConfigProvider theme={THEME}>
                    <CancelBtn />
                    <OkBtn />
                </ConfigProvider>
            ),
        });
    };

    const parseAndHandleError = async (err: any) => {
        try {
            let data: any = err?.data || err?.error || err;
            if (data instanceof Blob) {
                const text = await data.text();
                try {
                    data = JSON.parse(text);
                } catch {
                    data = { message: text };
                }
            }
            if (data?.code === 'INTAKE_OUTTAKE_MISMATCH') {
                return showMismatchModal(data?.intake_total, data?.outtake_total);
            }
        } catch {}
        messageApi.error('Failed to generate packet');
    };


    const handleDownloadReport = async (force = false) => {
        try {
            await triggerReport({ id: lineItemId, force }).unwrap();
        } catch (err) {
            await parseAndHandleError(err);
        }
    };

	return (
		<>
			{contextHolder}
			<Flex vertical align="start">
				<Text>Packet</Text>
				<Button
					icon={<DownloadOutlined />}
					type="primary"
					onClick={() => handleDownloadReport()}
					loading={isLoading}
					disabled={isDisabled}
					
				>
					{isLoading ? 'Generating...' : 'Download'}
				</Button>
				{error && (
					<Text type="danger" style={{ marginTop: 4 }}>
						Error generating report
					</Text>
				)}
			</Flex>
		</>
	);
};
